import { Box, Stack, Text } from '@mantine/core';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { useState } from 'react';

import {
  PartnerInvoiceType,
  useExtendInvoiceExpirationTime,
} from '@portals/api/partners';
import { DetailsPanel } from '@portals/core';
import {
  InvoicePaymentStatusBadge,
  InvoiceTimeline,
  ReceiptPanel,
  InvoicePaymentDetails,
  PurchaseOrderPanel,
  InvoiceItemsPanel,
  InvoiceAddressDetails,
  usePermissionAccess,
} from '@portals/framework';
import {
  formatCurrency,
  formatDateTime,
  getShortInvoiceId,
} from '@portals/utils';

import { CustomerDetails } from './CustomerDetails';
import { useUploadReceipt } from '../../../../hooks/Receipt/receipt.hooks';

dayjs.extend(relativeTime);

const EXTENSION_PERIOD_DAYS = 7;

interface InvoiceDetailsPanelProps {
  invoice: PartnerInvoiceType;
  onClose: () => void;
}

export function InvoiceDetailsPanel({
  invoice,
  onClose,
}: InvoiceDetailsPanelProps) {
  const [isItemsPanelOpen, setIsItemsPanelOpen] = useState(false);
  const [isReceiptPanelOpen, setIsReceiptPanelOpen] = useState(false);
  const [isPurchaseOrderPanelOpen, setIsPurchaseOrderPanelOpen] =
    useState(false);

  const uploadReceiptFile = useUploadReceipt(invoice?.id);
  const extendInvoiceExpirationTime = useExtendInvoiceExpirationTime();
  const { canEdit } = usePermissionAccess();

  const createdAtDate = formatDateTime(
    invoice?.created_at,
    'MMMM D, YYYY h:mm A'
  );
  const timeLeftToInvoiceExpiration = dayjs(invoice?.expired_at).fromNow();
  const isInvoiceExpirationTimePassed = dayjs().isAfter(
    dayjs(invoice?.expired_at)
  );

  const getPrimaryButton = () => {
    if (
      invoice?.payment_status === 'failed' &&
      !isInvoiceExpirationTimePassed
    ) {
      return canEdit(['finance']) ? (
        <DetailsPanel.ActionButton
          onClick={() =>
            extendInvoiceExpirationTime.mutate({
              invoiceId: invoice?.id,
              extensionPeriodDays: EXTENSION_PERIOD_DAYS,
            })
          }
          variant="filled"
        >
          Extend By 7 Days
        </DetailsPanel.ActionButton>
      ) : null;
    }

    if (invoice?.invoice_file_url) {
      return (
        <DetailsPanel.ActionButton
          onClick={() => setIsReceiptPanelOpen(!isReceiptPanelOpen)}
        >
          {isReceiptPanelOpen ? 'Hide Receipt' : 'Show Receipt'}
        </DetailsPanel.ActionButton>
      );
    } else {
      return (
        <DetailsPanel.ActionButton variant="filled" onClick={uploadReceiptFile}>
          Upload Receipt
        </DetailsPanel.ActionButton>
      );
    }
  };

  return (
    <>
      <ReceiptPanel
        isOpen={isReceiptPanelOpen}
        receiptId={invoice.id}
        fileUrl={invoice.invoice_file_url}
        onClose={() => setIsReceiptPanelOpen(false)}
      />

      <PurchaseOrderPanel
        isOpen={isPurchaseOrderPanelOpen}
        fileUrl={invoice.purchase_order?.file_url}
        orderId={invoice?.order_id}
        onClose={() => setIsPurchaseOrderPanelOpen(false)}
      />

      <InvoiceItemsPanel
        isOpen={isItemsPanelOpen}
        invoiceItems={invoice.items}
        currency={invoice.currency}
        itemsCount={invoice.items_count}
        onClose={() => setIsItemsPanelOpen(false)}
      />

      <DetailsPanel pos="relative" bg="white">
        <DetailsPanel.Header onClose={onClose} spacing="xxl">
          <Stack align="center">
            <Box>
              <DetailsPanel.Title align="center">
                <Text size="md">{getShortInvoiceId(invoice?.id)}</Text>
              </DetailsPanel.Title>

              <Text color="gray.5">{createdAtDate}</Text>

              {invoice?.expired_at && invoice?.payment_status === 'failed' && (
                <Text color="red" align="center">
                  Expire {timeLeftToInvoiceExpiration}
                </Text>
              )}
            </Box>

            <Text size={28}>
              {formatCurrency(invoice.amount_in_scu, invoice.currency)}
            </Text>
            <InvoicePaymentStatusBadge status={invoice.payment_status} />
          </Stack>

          <DetailsPanel.Actions>
            {getPrimaryButton()}
            <DetailsPanel.ActionButton
              onClick={() => setIsItemsPanelOpen(!isItemsPanelOpen)}
            >
              {isItemsPanelOpen ? 'Hide Items' : 'Show Items'}
            </DetailsPanel.ActionButton>
          </DetailsPanel.Actions>
        </DetailsPanel.Header>

        <DetailsPanel.Body>
          <CustomerDetails invoiceOrganization={invoice?.organization} />

          <InvoicePaymentDetails
            payment_method={invoice?.payment_method}
            currency={invoice?.currency}
            tax={invoice?.tax_in_scu}
            amount={invoice?.amount_in_scu}
            credit_card={invoice?.credit_card}
            onOpenPurchaseOrderPanel={() => setIsPurchaseOrderPanelOpen(true)}
          />

          <InvoiceTimeline
            invoiceCreatedAt={invoice?.created_at}
            purchaseOrderFile={invoice?.purchase_order?.file_url}
            purchaseOrderFileUploadedAt={
              invoice?.purchase_order?.file_uploaded_at
            }
            purchaseOrderApprovedAt={invoice?.order_paid_at}
            purchaseOrderApprovedBy={invoice?.purchase_order?.approved_by}
            receiptFile={invoice?.invoice_file_url}
            receiptFileUploadedAt={invoice?.invoice_file_uploaded_at}
            receiptFileUploadedBy={invoice?.invoice_file_uploaded_by}
            isPaymentReceive={invoice.payment_status === 'paid'}
            paymentReceivedAt={invoice?.order_paid_at}
          />

          <InvoiceAddressDetails
            country={invoice?.billing_address?.country}
            city={invoice?.billing_address?.city}
            address={invoice?.billing_address?.line_1}
            state={invoice?.billing_address?.state}
            zip={invoice?.billing_address?.zip}
            title="Billing address"
          />

          <InvoiceAddressDetails
            country={invoice?.shipping_address?.country}
            city={invoice?.shipping_address?.city}
            address={invoice?.shipping_address?.line_1}
            state={invoice?.shipping_address?.state}
            zip={invoice?.shipping_address?.zip}
            title="Shipping address"
          />
        </DetailsPanel.Body>
      </DetailsPanel>
    </>
  );
}
